import React from "react";
import { Search, ListFilter, X, RotateCcw, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TIER_IDS } from "@/lib/tiers";
import {
  EMPTY_FILTERS,
  hasActiveFilters,
  type InfluencerFilterState,
  type StatusFilterValue,
  type TierFilterValue,
} from "./utils";

const TIER_FILTER_LABELS: Record<TierFilterValue, string> = {
  starter: "Starter",
  oficial: "Oficial",
  elite: "Elite",
  top5: "Top 5 do Mês",
  none: "Sem categoria",
};

const STATUS_LABELS: Record<Exclude<StatusFilterValue, "all">, string> = {
  active: "Ativas",
  inactive: "Inativas",
};

interface FilterPillProps {
  label: string;
  onRemove: () => void;
}

const FilterPill: React.FC<FilterPillProps> = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
    {label}
    <button onClick={onRemove} className="hover:text-primary-foreground hover:bg-primary/60 rounded-full p-px transition-colors" title="Remover filtro">
      <X className="h-3 w-3" />
    </button>
  </span>
);

interface InfluencerFiltersProps {
  value: InfluencerFilterState;
  onChange: (next: InfluencerFilterState) => void;
  resultCount: number;
  totalCount: number;
}

export const InfluencerFilters: React.FC<InfluencerFiltersProps> = ({
  value,
  onChange,
  resultCount,
  totalCount,
}) => {
  const set = (patch: Partial<InfluencerFilterState>) => onChange({ ...value, ...patch });

  const toggleTier = (tier: TierFilterValue) => {
    set({
      tiers: value.tiers.includes(tier)
        ? value.tiers.filter((t) => t !== tier)
        : [...value.tiers, tier],
    });
  };

  const active = hasActiveFilters(value);
  const tierOptions: TierFilterValue[] = [...TIER_IDS, "none"];

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome ou cupom..."
            value={value.search}
            onChange={(e) => set({ search: e.target.value })}
            className="pl-8 w-60"
          />
        </div>

        {/* Tier multi-select */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={`border-white/10 bg-white/5 hover:bg-white/10 text-xs h-9 ${value.tiers.length > 0 ? "border-primary/50 text-primary" : ""}`}
            >
              <ListFilter className="h-3.5 w-3.5 mr-1.5" />
              Categoria
              {value.tiers.length > 0 && (
                <span className="ml-1.5 rounded-full bg-primary/20 px-1.5 text-[10px] font-semibold tabular-nums">
                  {value.tiers.length}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-52 p-1.5">
            {tierOptions.map((tier) => {
              const selected = value.tiers.includes(tier);
              return (
                <button
                  key={tier}
                  onClick={() => toggleTier(tier)}
                  className="w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-white/10 transition-colors"
                >
                  <span
                    className={`flex h-4 w-4 items-center justify-center rounded border transition-colors ${
                      selected ? "bg-primary border-primary" : "border-white/25"
                    }`}
                  >
                    {selected && <Check className="h-3 w-3 text-primary-foreground" />}
                  </span>
                  {TIER_FILTER_LABELS[tier]}
                </button>
              );
            })}
          </PopoverContent>
        </Popover>

        <Select value={value.status} onValueChange={(v) => set({ status: v as StatusFilterValue })}>
          <SelectTrigger className={`w-32 text-xs h-9 ${value.status !== "all" ? "border-primary/50 text-primary" : ""}`}>
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos status</SelectItem>
            <SelectItem value="active">Ativas</SelectItem>
            <SelectItem value="inactive">Inativas</SelectItem>
          </SelectContent>
        </Select>

        {/* Sales range */}
        <div className="flex items-center gap-1">
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            placeholder="Vendas mín."
            value={value.minSales}
            onChange={(e) => set({ minSales: e.target.value })}
            className="w-28 h-9 text-xs"
          />
          <span className="text-muted-foreground text-xs">–</span>
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            placeholder="máx."
            value={value.maxSales}
            onChange={(e) => set({ maxSales: e.target.value })}
            className="w-24 h-9 text-xs"
          />
        </div>

        {active && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onChange(EMPTY_FILTERS)}
            className="text-xs text-muted-foreground hover:text-foreground h-9"
          >
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
            Limpar
          </Button>
        )}

        <span className="text-xs text-muted-foreground ml-auto tabular-nums">
          {resultCount} de {totalCount}
        </span>
      </div>

      {/* Active filter pills */}
      {active && (
        <div className="flex flex-wrap items-center gap-1.5">
          {value.tiers.map((tier) => (
            <FilterPill key={tier} label={TIER_FILTER_LABELS[tier]} onRemove={() => toggleTier(tier)} />
          ))}
          {value.status !== "all" && (
            <FilterPill label={STATUS_LABELS[value.status]} onRemove={() => set({ status: "all" })} />
          )}
          {value.minSales !== "" && (
            <FilterPill label={`Vendas ≥ R$ ${value.minSales}`} onRemove={() => set({ minSales: "" })} />
          )}
          {value.maxSales !== "" && (
            <FilterPill label={`Vendas ≤ R$ ${value.maxSales}`} onRemove={() => set({ maxSales: "" })} />
          )}
        </div>
      )}
    </div>
  );
};

export default InfluencerFilters;
